import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { api, ErrorApi, type Partido, type Yo } from './services/api';
import { Entrar } from './screens/Entrar';
import { CambiarPin } from './screens/CambiarPin';
import { Inicio } from './screens/Inicio';
import { Cargar } from './screens/Cargar';
import { Tabla, type Solapa } from './screens/Tabla';
import { Perfil } from './screens/Perfil';
import { Historial } from './screens/Historial';
import { DetallePartido } from './screens/DetallePartido';
import { Admin } from './screens/Admin';
import { MiCuenta } from './screens/MiCuenta';
import { Ayuda } from './screens/Ayuda';
import { ResumenMes } from './screens/ResumenMes';
import { LogrosAdmin } from './screens/LogrosAdmin';
const GestionGrupos = lazy(() => import('./screens/GestionGrupos').then(m => ({ default: m.GestionGrupos })));
import { Aviso, BarraInferior, Boton, Cargando } from './components/ui';
import { CapaApp } from './pwa/CapaApp';
import { Marca } from './components/Marca';
import { IconoNavegacion } from './components/IconoNavegacion';
function recordarGrupo() {
  try { localStorage.setItem('rankealo.grupoConocido', 'si'); } catch { /* Navegación privada. */ }
}

// Sin router: pocas pantallas y se usa en el celular. Un `type Vista` alcanza,
// y evita una dependencia mas. Es el mismo patron de Soltura.
// 'admin' la ve solo quien es admin. La barra abre el perfil propio; los otros
// perfiles se abren tocando un nombre; 'cuenta', desde el ícono de la cabecera.
// 'historial' y 'partido', desde Inicio.
// 'ayuda', desde el "?" de arriba y desde Mi cuenta.
type Vista =
  | 'inicio'
  | 'cargar'
  | 'tabla'
  | 'admin'
  | 'cuenta'
  | 'perfil'
  | 'historial'
  | 'partido'
  | 'ayuda'
  | 'resumen'
  | 'logros-admin';

export function App() {
  const [gestionAbierta, setGestionAbierta] = useState(window.location.pathname === '/gestion');
  const [yo, setYo] = useState<Yo | null>(null);
  const [mirando, setMirando] = useState(true);
  const [vista, setVista] = useState<Vista>('inicio');
  // Abre en "Este mes": es la que el grupo mira. Con la general primero, cada
  // uno leia su puesto historico creyendo que era el del mes.
  const [solapaTabla, setSolapaTabla] = useState<Solapa>('mes');

  // El perfil que se esta mirando, y a donde vuelve "Volver". Se guarda solo la
  // pantalla de origen, no toda la cadena: si se salta de perfil en perfil
  // (compañero -> su rival -> ...), Volver lleva a la tabla y no hay que
  // desandar diez perfiles.
  const [perfilDe, setPerfilDe] = useState<number | null>(null);
  const [volverA, setVolverA] = useState<Vista>('inicio');

  const verPerfil = (id: number) => {
    if (vista !== 'perfil') setVolverA(vista);
    setPerfilDe(id);
    setVista('perfil');
  };

  // El partido abierto y de donde se vino (Inicio o el historial). El mes del
  // historial vive aca para que al volver del detalle siga en el mismo mes.
  const [partidoAbierto, setPartidoAbierto] = useState<number | null>(null);
  const [volverDePartido, setVolverDePartido] = useState<Vista>('inicio');
  const [mesHistorial, setMesHistorial] = useState<string | undefined>(undefined);

  const verPartido = (id: number) => {
    setVolverDePartido(vista);
    setPartidoAbierto(id);
    setVista('partido');
  };

  // El resumen de un mes cerrado: desde Este mes o desde el cartel de Inicio.
  const [resumenDe, setResumenDe] = useState<string | null>(null);
  const [volverDeResumen, setVolverDeResumen] = useState<Vista>('inicio');

  const verResumen = (mes: string) => {
    setVolverDeResumen(vista);
    setResumenDe(mes);
    setVista('resumen');
  };

  const [partidos, setPartidos] = useState<{
    confirmados: Partido[];
    pendientes: Partido[];
  } | null>(null);
  const [cargandoPartidos, setCargandoPartidos] = useState(false);
  const [errorPartidos, setErrorPartidos] = useState(false);
  const consultaPartidos = useRef(0);

  // ── Quien esta adentro ──
  const revisarSesion = useCallback(async () => {
    setMirando(true);
    try {
      setYo(await api.yo());
      recordarGrupo();
    } catch {
      setYo(null); // sin sesion, o vencida
    } finally {
      setMirando(false);
    }
  }, []);

  useEffect(() => {
    void revisarSesion();
  }, [revisarSesion]);

  // Despues de un cambio (confirmar, renombrarse, cambiar el PIN) se relee
  // quien soy SIN pasar por "Entrando...": eso desmontaria la pantalla y se
  // perderia el mensaje de "listo" antes de que alguien lo lea. Cambiar `yo`
  // ya dispara la recarga de partidos (ver el efecto de abajo).
  const refrescarYo = useCallback(async () => {
    try {
      setYo(await api.yo());
    } catch (e) {
      if (e instanceof ErrorApi && e.estado === 401) setYo(null);
    }
  }, []);

  // ── Partidos ──
  const traerPartidos = useCallback(async () => {
    const consulta = ++consultaPartidos.current;
    setCargandoPartidos(true);
    setErrorPartidos(false);
    try {
      const nuevos = await api.partidos();
      // Una lectura anterior a confirmar no debe volver a mostrar el pendiente.
      if (consulta === consultaPartidos.current) setPartidos(nuevos);
    } catch (e) {
      // Si la sesion vencio mientras estaba abierta, se vuelve a la entrada en
      // vez de dejar una pantalla rota.
      if (consulta === consultaPartidos.current && e instanceof ErrorApi && e.estado === 401) setYo(null);
      else if (consulta === consultaPartidos.current) setErrorPartidos(true);
    } finally {
      if (consulta === consultaPartidos.current) setCargandoPartidos(false);
    }
  }, []);

  useEffect(() => {
    if (yo && !yo.debe_cambiar_pin) void traerPartidos();
  }, [yo, traerPartidos]);

  // La app instalada queda abierta en el celular dias enteros. Sin esto, el
  // partido que carga un rival no aparece hasta cerrarla y abrirla, y el
  // cartel de "tenes para confirmar" llega tarde -- paso la primera noche.
  useEffect(() => {
    if (!yo || yo.debe_cambiar_pin) return;
    const alVolver = () => {
      if (document.visibilityState === 'visible') void traerPartidos();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [yo, traerPartidos]);

  // Ir a Inicio o a la Tabla tambien relee: son las dos pantallas donde se
  // mira si un partido ya cuenta.
  const irA = (v: Vista) => {
    setVista(v);
    if (v === 'inicio' || v === 'tabla') void traerPartidos();
  };

  if (mirando) {
    return (
      <main className="flex min-h-full items-center justify-center">
        <Cargando que="Entrando" />
      </main>
    );
  }

  if (!yo) {
    return (
      <>
        <Entrar onEntro={revisarSesion} />
        <CapaApp conBarraAbajo={false} />
      </>
    );
  }

  // Mientras tenga el PIN provisorio, el Worker bloquea todo lo demas (428).
  // La app lo acompana en vez de dejarlo chocar contra el error.
  if (yo.debe_cambiar_pin) {
    return (
      <>
        <CambiarPin obligatorio onListo={revisarSesion} />
        <CapaApp conBarraAbajo={false} />
      </>
    );
  }

  const pendientesMios = partidos?.pendientes.filter((p) => p.puedo_confirmar).length ?? 0;
  const pendientesTodos = partidos?.pendientes.length ?? 0;

  if (gestionAbierta) {
    const volver = () => { setGestionAbierta(false); window.history.replaceState(null, '', '/'); };
    if (!yo.es_dueno) return <main className="mx-auto max-w-md p-5"><p>Este espacio es exclusivo del dueño de Rankealo.</p><button className="mt-4 text-(--color-acento) underline" onClick={volver}>Volver a mi grupo</button></main>;
    return <Suspense fallback={<main className="p-5"><Cargando que="Abriendo tus grupos" /></main>}><GestionGrupos onVolver={volver} /></Suspense>;
  }

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col">
      <header data-vista={vista} className="cabecera-app sticky top-0 z-20 flex h-14 shrink-0 items-center justify-between border-b border-(--color-borde) bg-(--color-panel) px-5">
        <h1>
          <Marca />
        </h1>
        <div className="flex items-center">
          <button
            onClick={() => setVista('ayuda')}
            aria-label="Preguntas frecuentes"
            className="flex h-11 w-11 items-center justify-center text-(--color-acento)"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-sm font-bold">?</span>
          </button>
          <button
            onClick={() => setVista('cuenta')}
            aria-label="Mi cuenta"
            title={yo.jugador.nombre}
            className="flex h-11 w-11 items-center justify-center text-(--color-acento)"
          >
            <IconoNavegacion tipo="perfil" />
          </button>
        </div>
      </header>

      {/* En Inicio ya esta el cartel grande; en el resto de las pantallas
          esto lo lleva ahi de un toque. */}
      {pendientesMios > 0 && vista !== 'inicio' && (
        <button
          onClick={() => irA('inicio')}
          className="mx-5 mb-3 flex items-center justify-between gap-3 rounded-xl
            bg-(--color-alerta) px-4 py-3 text-left text-sm font-semibold text-(--color-sobre-acento)"
        >
          <span>
            {pendientesMios === 1
              ? 'Tenés un partido para confirmar'
              : `Tenés ${pendientesMios} partidos para confirmar`}
          </span>
          <span className="shrink-0">Confirmar →</span>
        </button>
      )}

      <div className={`flex-1 px-5 pb-6 ${vista === 'perfil' ? '' : 'pt-3'}`}>
        {vista === 'inicio' && errorPartidos && <Aviso tono="alerta">
          <p>No pudimos actualizar los partidos. La lista puede haber cambiado.</p>
          <Boton chico tipo="secundario" onClick={() => void traerPartidos()}>Volver a intentar</Boton>
        </Aviso>}
        {vista === 'inicio' && (
          <Inicio
            yo={yo}
            partidos={partidos}
            cargando={cargandoPartidos}
            onCambio={traerPartidos}
            onIrACargar={() => irA('cargar')}
            onVerPerfil={verPerfil}
            onVerPartido={verPartido}
            onVerHistorial={() => setVista('historial')}
            onVerResumen={verResumen}
            onVerMes={() => { setSolapaTabla('mes'); irA('tabla'); }}
          />
        )}

        {vista === 'historial' && (
          <Historial
            yo={yo}
            mes={mesHistorial}
            setMes={setMesHistorial}
            onVolver={() => irA('inicio')}
            onVerPartido={verPartido}
          />
        )}

        {vista === 'partido' && partidoAbierto !== null && (
          <DetallePartido
            partidoId={partidoAbierto}
            yo={yo}
            onCambio={traerPartidos}
            onVolver={() => setVista(volverDePartido)}
          />
        )}

        {vista === 'cargar' && (
          <Cargar
            yo={yo}
            onCargado={() => irA('inicio')}
          />
        )}

        {vista === 'tabla' && (
          <Tabla
            yo={yo}
            solapa={solapaTabla}
            setSolapa={setSolapaTabla}
            onVerPerfil={verPerfil}
            onVerResumen={verResumen}
            sinConfirmar={pendientesTodos}
          />
        )}

        {vista === 'resumen' && resumenDe !== null && (
          <ResumenMes
            mes={resumenDe}
            yo={yo}
            onVolver={() => setVista(volverDeResumen)}
            onVerPerfil={verPerfil}
          />
        )}

        {vista === 'perfil' && perfilDe !== null && (
          <Perfil
            jugadorId={perfilDe}
            yo={yo}
            onVolver={() => setVista(volverA)}
            onVerPerfil={verPerfil}
          />
        )}

        {vista === 'admin' && yo.jugador.rol === 'admin' && <Admin yo={yo} onVerPartido={verPartido} onLogros={() => setVista('logros-admin')} />}
        {vista === 'logros-admin' && yo.jugador.rol === 'admin' && <LogrosAdmin onVolver={() => setVista('admin')} />}

        {vista === 'cuenta' && (
          <MiCuenta
            yo={yo}
            onCambio={() => void refrescarYo()}
            onVolver={() => setVista('inicio')}
            onAyuda={() => setVista('ayuda')}
            onGestion={() => { setGestionAbierta(true); window.history.replaceState(null, '', '/gestion'); }}
            onSalir={async () => { await api.salir(); setYo(null); setPartidos(null); }}
          />
        )}

        {vista === 'ayuda' && <Ayuda onVolver={() => setVista('inicio')} />}
      </div>

      <CapaApp conBarraAbajo />

      <BarraInferior
        vista={vista}
        irA={irA}
        pendientes={pendientesMios}
        esAdmin={yo.jugador.rol === 'admin'}
        miPerfil={vista === 'perfil' && perfilDe === yo.jugador.id}
        onMiPerfil={() => { verPerfil(yo.jugador.id); window.scrollTo(0, 0); }}
      />
    </div>
  );
}
